import { BIndexThresholdType, QuestionStatus } from '@report/graphql';
import { dayjs } from '@shared/utils';
import { z } from 'zod';

import { MONTH_ORDER } from '../../constants';

export const itemAnalysisYearlyFilterFormSchema = z
  .object({
    startMonth: z
      .object(
        {
          label: z.string(),
          value: z.enum(MONTH_ORDER),
        },
        { message: 'กรุณาเลือก Month' },
      )
      .nullable()
      .refine((val) => !!val, { message: 'กรุณาเลือก Month' }),
    startYear: z
      .object(
        {
          value: z.number(),
          label: z.string(),
        },
        {
          message: 'กรุณาเลือก Year',
        },
      )
      .nullable()
      .refine((val) => !!val, { message: 'กรุณาเลือก Year' }),
    endMonth: z
      .object(
        {
          label: z.string(),
          value: z.enum(MONTH_ORDER),
        },
        { message: 'กรุณาเลือก Month' },
      )
      .nullable()
      .refine((val) => !!val, { message: 'กรุณาเลือก Month' }),
    endYear: z
      .object(
        {
          value: z.number(),
          label: z.string(),
        },
        {
          message: 'กรุณาเลือก Year',
        },
      )
      .nullable()
      .refine((val) => !!val, { message: 'กรุณาเลือก Year' }),
    examCourse: z.object(
      {
        value: z.string(),
        label: z.string(),
      },
      { message: 'กรุณาเลือกคลังข้อสอบ' },
    ),
    questionStatus: z
      .array(
        z.object({
          value: z.nativeEnum(QuestionStatus),
          label: z.string(),
        }),
        { message: 'กรุณาเลือกสถานะคำถาม' },
      )
      .min(1, { message: 'กรุณาเลือกสถานะคำถาม' }),
    minExaminees: z
      .number({ message: 'กรุณาระบุจำนวนผู้เข้าสอบขั้นต่ำ' })
      .refine((val) => val === 0 || val >= 300, {
        message: 'จำนวนผู้เข้าสอบต้องไม่น้อยกว่า 300 คน หรือกรอก 0 เพื่อเลือกทั้งหมด',
      }),
    bIndex: z.object({
      knowledgeGroup: z
        .object({
          knowledgeGroupSelection: z.nativeEnum(BIndexThresholdType),
          customThreshold: z.preprocess((val) => {
            if (val === '' || val === undefined || val === null) {
              return undefined;
            }

            return Number(val);
          }, z.number().optional()),
        })
        .refine(
          (val) => {
            if (val.knowledgeGroupSelection === BIndexThresholdType.UseExamResult) {
              return true;
            }

            if (val.knowledgeGroupSelection === BIndexThresholdType.CustomThreshold) {
              return val.customThreshold !== undefined;
            }

            return false;
          },
          {
            message: '',
            path: ['customThreshold'],
          },
        ),
      nonKnowledgeGroup: z
        .object({
          nonKnowledgeGroupSelection: z.nativeEnum(BIndexThresholdType),
          customThreshold: z.preprocess((val) => {
            if (val === '' || val === undefined || val === null) {
              return undefined;
            }

            return Number(val);
          }, z.number().optional()),
        })
        .refine(
          (val) => {
            if (val.nonKnowledgeGroupSelection === BIndexThresholdType.UseExamResult) {
              return true;
            }

            if (val.nonKnowledgeGroupSelection === BIndexThresholdType.CustomThreshold) {
              return val.customThreshold !== undefined;
            }

            return false;
          },
          {
            message: '',
            path: ['customThreshold'],
          },
        ),
    }),
  })
  .refine(
    (data) => {
      if (!data.startMonth || !data.endMonth) {
        return true;
      }

      const startDate = dayjs()
        .set('month', MONTH_ORDER.indexOf(data.startMonth.value))
        .set('year', data.startYear.value);
      const endDate = dayjs()
        .set('month', MONTH_ORDER.indexOf(data.endMonth.value))
        .set('year', data.endYear.value);

      return endDate.isSameOrAfter(startDate);
    },
    {
      message: 'End Month ต้องมากกว่าหรือเท่ากับ Start Month',
      path: ['endMonth'],
    },
  );

export type ItemAnalysisYearlyFilterFormInput = z.infer<typeof itemAnalysisYearlyFilterFormSchema>;
